import { zodResolver } from '@hookform/resolvers/zod';
import { FormSectionTitle } from 'atoms/FormSectionTitle';
import { PhoneNumberSchemaType } from 'commons/auditionForm';
import { usePhoneVerificationSchema } from 'hooks/u002/usePhoneVerificationSvhema';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { useContext, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AppContext } from 'templates/AppContextProvider';
import { AppLogoHeader } from 'templates/AppLogoHeader';
import { PageContent } from 'templates/PageContent';
import { UnauthAppFooter } from 'templates/UnauthAppFooter';
import { Country, getCountryCallingCode } from 'react-phone-number-input';
import { SelectCountryNumber } from 'atoms/SelectCountryNumber';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { Button } from 'atoms/Button';
import { useState } from 'react';
import { Form } from 'templates/Form';
import { useUserIdConfirmination } from 'hooks/u001/useUserIdConfirminationHooks';
import LoadingModal from 'organisms/LoadingModal';
import MessageModal from 'organisms/MessageModal';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'commons/paths';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { BackButton } from 'atoms/BackButton';
import ReCaptchaErrorModal from 'organisms/ReCaptchaErrorModal';

export function ForgetUserId() {
  const { currentLang, isGlobal } = useContext(AppContext);
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');
  const PhoneVerificationSchema = usePhoneVerificationSchema();
  const [userIdConfirmination, isLoading] = useUserIdConfirmination();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [countryCode, setCountryCode] = useState(isGlobal ? 'US' : 'JP');
  const { t: tForgetUserId } = useTranslationWithLang('forgetUserId');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showRecaptchaModal, setShowRecaptchaModal] = useState(false);
  const {
    setValue,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<PhoneNumberSchemaType>({
    resolver: zodResolver(PhoneVerificationSchema),
    shouldFocusError: true,
    defaultValues: {
      phoneNumber: '',
    },
  });

  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);

  const onSubmit: SubmitHandler<PhoneNumberSchemaType> = async (form) => {
    console.log({ form });
    if (!executeRecaptcha) {
      setErrorMessage(tAuditionForm('TU002_108'));
      setShowModal(true);
      return;
    }
    const recaptchaToken = await executeRecaptcha('forget_user_id');
    await userIdConfirmination({
      request: {
        phoneNumber: form.phoneNumber,
        lang: currentLang,
        recaptcha_token: recaptchaToken,
      },
      onSuccess: () => {
        setShowModal(true);
      },
      onError: (error) => {
        console.error(error);
        if (error instanceof Error) {
          if (error.message === 'reCAPTCHA verification failed') {
            setShowRecaptchaModal(true);
            return;
          }
          setErrorMessage(tForgetUserId('TU015_006'));
        }
      },
    });
  };

  return (
    <>
      {isLoading && <LoadingModal />}
      {showModal && (
        <MessageModal
          message={tForgetUserId('TU015_001')}
          closeLabel={tForgetUserId('TU015_002')}
          onClose={() => {
            navigate(PATHS.LOGIN);
          }}
        />
      )}
      {errorMessage && (
        <MessageModal
          message={errorMessage}
          closeLabel={tForgetUserId('TU015_003')}
          onClose={() => {
            setErrorMessage('');
          }}
        />
      )}
      {showRecaptchaModal && <ReCaptchaErrorModal onClose={() => setShowRecaptchaModal(false)} />}
      <AppLogoHeader />
      <div role="heading" aria-level={1} aria-live="assertive" className="sr-only">
        {tForgetUserId('TU015_004')}
      </div>
      <PageContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form title={tForgetUserId('TU015_004')}>
            <div className="flex flex-col items-center justify-center w-full">
              {/* 日本の電話番号表示（国識別番号表示なし） */}
              {!isGlobal && (
                <>
                  <div className="w-full mx-auto max-w-[18rem]">
                    <FormSectionTitle text={tAuditionForm('TU002_012')} htmlFor={'phoneNumber'} />
                  </div>
                  <div className="flex items-center justify-start w-full max-w-[18rem] mx-auto mt-2">
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      inputMode="numeric"
                      className="flex-grow border-[--c-textinput-border] text-[--c-textinput-text] bg-[--c-textinput-background] rounded-[8px] h-[40px] px-2 border-[1px]"
                      placeholder="08012345678"
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const countryCallingCode = getCountryCallingCode(countryCode as Country);
                        const formattedValue = inputValue
                          ? `+${countryCallingCode}${inputValue}`
                          : '';
                        setValue('phoneNumber', formattedValue, { shouldValidate: true });
                      }}
                    />
                  </div>
                </>
              )}
              {/* 海外の電話番号表示（国識別番号表示あり） */}
              {isGlobal && (
                <>
                  <div className="w-full max-w-md mx-auto">
                    <FormSectionTitle text={tAuditionForm('TU002_012')} htmlFor={'phoneNumber'} />
                  </div>
                  <div className="flex items-center justify-start w-full max-w-md mx-auto mt-2">
                    <SelectCountryNumber
                      countryCode={countryCode}
                      setCountryCode={(code) => {
                        setCountryCode(code);
                        // 国選択後に既存の入力値があれば、国番号を更新
                        const phoneNumberValue = document.getElementById(
                          'phoneNumber'
                        ) as HTMLInputElement;
                        if (phoneNumberValue && phoneNumberValue.value) {
                          const countryCallingCode = getCountryCallingCode(code as Country);
                          const formattedValue = phoneNumberValue.value
                            ? `+${countryCallingCode}${phoneNumberValue.value}`
                            : '';
                          setValue('phoneNumber', formattedValue, { shouldValidate: true });
                        }
                      }}
                      className="w-32 text-[--c-textinput-text] bg-[--c-textinput-background] mr-2"
                    />
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      inputMode="numeric"
                      className="flex-grow border-[--c-textinput-border] text-[--c-textinput-text] bg-[--c-textinput-background] rounded-[8px] h-[40px] px-2 border-[1px]"
                      placeholder="08012345678"
                      onChange={(e) => {
                        const countryCallingCode = getCountryCallingCode(countryCode as Country);
                        const formattedValue = e.target.value
                          ? `+${countryCallingCode}${e.target.value}`
                          : '';
                        setValue('phoneNumber', formattedValue, { shouldValidate: true });
                      }}
                    />
                  </div>
                </>
              )}
              <FieldErrorMessages messages={[errors.phoneNumber?.message]} />
              <div className="flex justify-center items-center h-10 my-10 mt-20">
                <BackButton className="mr-2 w-[9rem]" />
                <Button className="w-[9rem]" label={tForgetUserId('TU015_005')} type="submit" />
              </div>
            </div>
          </Form>
        </form>
      </PageContent>
      <UnauthAppFooter />
    </>
  );
}
